import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

/**
 * GTM CONFIG (ONLY THING YOU MAINTAIN)
 */
const GTM_CONFIG: Record<string, string> = {
  // LIVE
  'skyporter.us': 'GTM-WB8Q8PGC',
  'www.skyporter.us': 'GTM-WB8Q8PGC',

  // QA
  'skyporter-us-qa.azurewebsites.net': 'GTM-PVG4D24R',
}

const hostname = window.location.hostname
const GTM_ID = GTM_CONFIG[hostname]

const shouldEnableGtm =
  !!GTM_ID && window.location.protocol === 'https:'

/**
 * Inject GTM (official snippet preserved)
 */
function injectGtm(gtmId: string) {
  if (document.getElementById('gtm-script')) return

  window.dataLayer = window.dataLayer || []

  // GTM SCRIPT (HEAD)
  const script = document.createElement('script')
  script.id = 'gtm-script'
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];
    w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
    var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
    j.async=true;
    j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `
  document.head.prepend(script)

  // NOSCRIPT (BODY TOP)
  const noscript = document.createElement('noscript')
  noscript.id = 'gtm-noscript'
  noscript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `
  document.body.prepend(noscript)

  // INITIAL PAGE VIEW
  window.dataLayer.push({
    event: 'virtual_page_view',
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
    page_location: window.location.href,
  })
}

/**
 * SPA NAVIGATION TRACKING (FIX FOR ABOUT / OTHER PAGES)
 */
function setupRouteTracking() {
  let lastPath = location.pathname + location.search

  const pushPageView = () => {
    const currentPath = location.pathname + location.search

    if (currentPath !== lastPath) {
      lastPath = currentPath

      window.dataLayer?.push({
        event: 'virtual_page_view',
        page_path: currentPath,
        page_title: document.title,
        page_location: window.location.href,
      })
    }
  }

  // Detect navigation changes
  const originalPushState = history.pushState
  history.pushState = function (...args) {
    originalPushState.apply(this, args)
    pushPageView()
  }

  const originalReplaceState = history.replaceState
  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args)
    pushPageView()
  }

  window.addEventListener('popstate', pushPageView)
}

/**
 * INIT
 */
if (shouldEnableGtm) {
  injectGtm(GTM_ID)
  setupRouteTracking()
}

/**
 * React App
 */
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)